import { showcaseProductSizeToSizeId, sizeIdToMl } from "./cartItem";
import { computeCartLinePrice } from "./pricing";
import { INGREDIENTS } from "./ingredients";

export { INGREDIENTS };
export { computeBlendPrice, BOTANICAL_ADDON_GBP } from "./pricing";

/**
 * @param {'gold'|'purple'} base
 * @param {string} sizeLabel
 * @param {'plain'|'custom'} type
 * @param {'jar'|'pouch'} packaging
 */
function showcasePrice(base, sizeLabel, type, packaging) {
  const sizeId = showcaseProductSizeToSizeId(sizeLabel);
  const size_ml = sizeIdToMl(sizeId) ?? 500;
  return computeCartLinePrice({
    base_type: base,
    size_ml,
    product_type: type,
    herbs: [],
    packaging_type: packaging,
  });
}

export const UK_TRUST_LINES = {
  prepared: "Freshly prepared in the UK",
  delivery: "UK orders only",
  prepTime:
    "Fresh gel orders are usually prepared within about 1–3 working days, then dispatched on UK tracked services. At peak times we may need the full window before dispatch, and kitchen scheduling can take up to 48 hours after checkout on busy days.",
  bestPrice: "Our best prices are always here on mossmatrix.com",
  trialFrom: "200ml trial foil pouches from £5.99",
  customJars:
    "Custom blends are being simplified — start with plain Gold or Purple. Selected mix-ins may return later.",
  jarLine: "Premium fridge-ready glass jars. The classic Moss Matrix feel in your fridge.",
  pouchLine:
    "Foil pouches are our best-value packaging: lighter, easier to deliver, and often the most affordable way to order.",
  deliveryCheckout:
    "Standard UK delivery is £3.99. Priority or tracked delivery is available at checkout if you want extra peace of mind.",
};

/** Builder volume steps (jar or pouch chosen separately). */
export const BUILDER_VOLUME_SIZES = [
  { id: "200ml", label: "200 ml", sub: "Trial · foil pouch · plain only" },
  { id: "300ml", label: "300 ml", sub: "Jar or pouch · plain only" },
  { id: "500ml", label: "500 ml", sub: "Jar or pouch · plain base, or +£1.50 per herb (max 3)" },
  { id: "1L", label: "1 Litre", sub: "Jar or pouch · plain base, or +£1.50 per herb (max 3)" },
];

/** Homepage / shop display — plain gold trial bundle (checkout: add 3 × 200ml trial pouches). */
export const PLAIN_STARTER_PACK_OFFER = {
  id: "plain-starter-pack",
  title: "Plain Starter Pack",
  subtitle: "3 × 200ml foil pouches",
  priceLabel: "£18.99",
  priceGbp: 18.99,
  tagline: "Best for trying Moss Matrix for the first time.",
  description:
    "Plain sea moss only. Easy delivery, lower breakage risk, and a simple way to try the routine before buying a larger jar or pouch.",
  cta: "Shop starter pack",
  shopPath: "/shop#trial-pouches",
};

/** Label lookup for blender, cart copy, and legacy 250ml carts. */
export const SIZES = [...BUILDER_VOLUME_SIZES, { id: "250ml", label: "250 ml", sub: "Legacy trial pouch" }];

export const PRODUCTS = [
  {
    id: "trial-gold-200",
    group: "trial",
    packaging: "pouch",
    name: "Gold Sea Moss",
    size: "200ml trial foil pouch",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "200ml trial foil pouch", "plain", "pouch"),
    tag: "Best value · trial pouch",
    cardNote: "Plain sea moss gel · lighter pouch · easy to store",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "trial-purple-200",
    group: "trial",
    packaging: "pouch",
    name: "Purple Sea Moss",
    size: "200ml trial foil pouch",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "200ml trial foil pouch", "plain", "pouch"),
    tag: "Best value · trial pouch · premium base",
    cardNote: "Plain sea moss gel · lighter pouch · easy to store",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "plain-pouch-250",
    group: "trial",
    packaging: "pouch",
    name: "Gold Sea Moss (legacy 250ml)",
    size: "250ml foil zip pouch",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "250ml foil zip pouch", "plain", "pouch"),
    tag: "Plain only · trial",
    cardNote: "Same trial idea as 200ml — while supplies last",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-plain-pouch-250",
    group: "trial",
    packaging: "pouch",
    name: "Purple Sea Moss (legacy 250ml)",
    size: "250ml foil zip pouch",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "250ml foil zip pouch", "plain", "pouch"),
    tag: "Plain only · trial · premium base",
    cardNote: "Same trial idea as 200ml — while supplies last",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "plain-300",
    group: "standard",
    packaging: "jar",
    name: "Gold Sea Moss",
    size: "300ml",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "300ml", "plain", "jar"),
    tag: "Premium · fridge-ready jar",
    image: "https://images.unsplash.com/photo-1559881230-1af605ca3f67?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "gold-300-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Gold Sea Moss",
    size: "300ml",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "300ml", "plain", "pouch"),
    tag: "Best value · foil pouch",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-plain-300",
    group: "standard",
    packaging: "jar",
    name: "Purple Sea Moss",
    size: "300ml",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "300ml", "plain", "jar"),
    tag: "Premium fridge-ready jar · premium base",
    image: "https://images.unsplash.com/photo-1727839202150-65675fbd0027?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-300-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Purple Sea Moss",
    size: "300ml",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "300ml", "plain", "pouch"),
    tag: "Best value · foil pouch · premium base",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "plain-500",
    group: "standard",
    packaging: "jar",
    name: "Gold Sea Moss",
    size: "500ml",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "500ml", "plain", "jar"),
    tag: "Premium · fridge-ready jar",
    image: "https://images.pexels.com/photos/8789618/pexels-photo-8789618.jpeg?auto=compress&cs=tinysrgb&w=700&q=80",
  },
  {
    id: "gold-500-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Gold Sea Moss",
    size: "500ml",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "500ml", "plain", "pouch"),
    tag: "Best value · foil pouch",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-plain-500",
    group: "standard",
    packaging: "jar",
    name: "Purple Sea Moss",
    size: "500ml",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "500ml", "plain", "jar"),
    tag: "Premium fridge-ready jar · premium base",
    image: "https://images.unsplash.com/photo-1727839202150-65675fbd0027?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-500-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Purple Sea Moss",
    size: "500ml",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "500ml", "plain", "pouch"),
    tag: "Lighter, safer delivery pouch · premium base",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "plain-1L",
    group: "standard",
    packaging: "jar",
    name: "Gold Sea Moss",
    size: "1 Litre",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "1 Litre", "plain", "jar"),
    tag: "Premium · fridge-ready jar",
    image: "https://images.unsplash.com/photo-1674864728789-d2690891d9f5?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "gold-1L-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Gold Sea Moss",
    size: "1 Litre",
    type: "plain",
    base_type: "gold",
    price: showcasePrice("gold", "1 Litre", "plain", "pouch"),
    tag: "Best value · foil pouch",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-plain-1L",
    group: "standard",
    packaging: "jar",
    name: "Purple Sea Moss",
    size: "1 Litre",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "1 Litre", "plain", "jar"),
    tag: "Premium fridge-ready jar · premium base",
    image: "https://images.unsplash.com/photo-1727839202150-65675fbd0027?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "purple-1L-pouch",
    group: "standard",
    packaging: "pouch",
    name: "Purple Sea Moss",
    size: "1 Litre",
    type: "plain",
    base_type: "purple",
    price: showcasePrice("purple", "1 Litre", "plain", "pouch"),
    tag: "Lighter, safer delivery pouch · premium base",
    image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=700&q=80",
  },
  {
    id: "custom-500",
    group: "standard",
    packaging: "jar",
    name: "Custom Blend",
    size: "500ml",
    type: "custom",
    base_type: "gold",
    price: showcasePrice("gold", "500ml", "custom", "jar"),
    tag: "From plain base + £1.50 per botanical · up to 3",
    image: "https://images.pexels.com/photos/8789618/pexels-photo-8789618.jpeg?auto=compress&cs=tinysrgb&w=700&q=80",
  },
  {
    id: "custom-1L",
    group: "standard",
    packaging: "jar",
    name: "Custom Blend",
    size: "1 Litre",
    type: "custom",
    base_type: "gold",
    price: showcasePrice("gold", "1 Litre", "custom", "jar"),
    tag: "From plain base + £1.50 per botanical · up to 3",
    image: "https://images.unsplash.com/photo-1674864728789-d2690891d9f5?auto=format&fit=crop&w=700&q=80",
  },
];

export const HERO_IMAGE =
  "https://images.pexels.com/photos/8789618/pexels-photo-8789618.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80";

const P = process.env.PUBLIC_URL || "";

/** Homepage hero — product still life (primary). */
export const HERO_HOME_IMAGE = `${P}/images/hero-sea-moss-jar-pouch-botanicals.png`;
export const HERO_HOME_IMAGE_FALLBACK = `${P}/images/stories-hero-jar-stone.png`;

/**
 * Homepage landing hero slideshow (legacy multi-slide config).
 * The live homepage Hero uses a single product-led composition; these slides
 * remain available if a carousel is restored later.
 */
export const HERO_HOME_SLIDES = [
  {
    id: "from-the-sea",
    backgroundImage: `${P}/images/lifestyle/caribbean-coastal-turquoise.jpg`,
    backgroundFallback: `${P}/images/lifestyle/tropical-ocean-horizon.jpg`,
    rightImage: HERO_HOME_IMAGE,
    rightImageFallback: HERO_HOME_IMAGE_FALLBACK,
    rightImageAlt: "Moss Matrix Gold and Purple Sea Moss Gel jar and pouch",
    eyebrow: "Moss Matrix",
    headline: "Fresh from the sea",
    sub: "",
    showCta: true,
    showPrice: true,
  },
  {
    id: "serene-still-life",
    backgroundImage: `${P}/images/lifestyle/serene-sea-moss-gel-still-life.png`,
    backgroundFallback: HERO_HOME_IMAGE_FALLBACK,
    eyebrow: "",
    headline: "Simply presented",
    sub: "",
  },
  {
    id: "morning-spoon",
    backgroundImage: `${P}/images/lifestyle/morning-spoon-routine.jpg`,
    backgroundFallback: HERO_HOME_IMAGE_FALLBACK,
    eyebrow: "",
    headline: "Quiet mornings",
    sub: "",
  },
  {
    id: "coastal-horizon",
    backgroundImage: `${P}/images/lifestyle/tropical-ocean-horizon.jpg`,
    backgroundFallback: `${P}/images/lifestyle/caribbean-coastal-turquoise.jpg`,
    eyebrow: "",
    headline: "Coastal freshness",
    sub: "",
  },
  {
    id: "blend-ready",
    backgroundImage: `${P}/images/lifestyle/smoothie-oats-dark.jpg`,
    backgroundFallback: HERO_HOME_IMAGE_FALLBACK,
    eyebrow: "",
    headline: "Blend · stir · spoon",
    sub: "",
  },
];

/**
 * Claim-safe homepage benefits marquee phrases.
 * Soft food / lifestyle language only — aligned with Learn/SeaMossBenefitsPage
 * (no disease prevention, cure, treat, immunity, detox, or hard gut claims).
 */
export const HOME_BENEFITS_MARQUEE = [
  "Mineral-rich sea moss",
  "Everyday wellness routines",
  "Traditionally enjoyed in coastal kitchens",
  "Fridge-ready daily habit",
  "Freshly prepared gel",
  "Stir into smoothies & oats",
  "Gold or Purple",
  "Simple kitchen ritual",
  "Wildcrafted · spring water",
  "Made for quiet mornings",
];

/** Primary circular brand mark (header, footer, hero, favicon, OG defaults, jar label). */
export const BRAND_LOGO_LOCKUP = `${P}/images/moss-matrix-logo-circle.png`;

/** Lifestyle brand photography — real Moss Matrix packaging / identity stills. */
export const BRAND_CARD_IMAGE = `${P}/images/brand/moss-matrix-brand-card.png`;

/** Wide brand creative banners for homepage (keep proportional; do not crop as a hero slide). */
export const BRAND_BANNER_IMAGE = `${P}/images/brand/pure-raw-wildcrafted-banner.png`;

/** Homepage brand banner slideshow — add `{ id, src, alt }` entries to include more slides. */
export const BRAND_BANNER_SLIDES = [
  {
    id: "pure-raw-wildcrafted",
    src: BRAND_BANNER_IMAGE,
    alt: "Moss Matrix Gold Sea Moss Gel brand banner — jar with water splash and fresh produce styling",
  },
  {
    id: "natures-superfood",
    src: `${P}/images/brand/natures-superfood-banner.png`,
    alt: "Moss Matrix Nature's Superfood brand banner — jar with botanicals and wellness styling",
  },
  {
    id: "choose-your-base",
    src: `${P}/images/brand/choose-your-base-banner.png`,
    alt: "Moss Matrix Choose Your Base brand banner",
  },
];

/** Same asset as lockup — jar label and JSON-LD `logo` use the circular mark. */
export const BRAND_LOGO = `${P}/images/moss-matrix-logo-circle.png`;

export const JAR_REFERENCE = `${P}/images/stories-hero-jar-stone.png`;

export const EDITORIAL = {
  morningRitual:
    "https://images.pexels.com/photos/8789618/pexels-photo-8789618.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80",
  gutHealth:
    "https://images.unsplash.com/photo-1559881230-1af605ca3f67?auto=format&fit=crop&w=900&q=80",
  dailyWellness:
    "https://images.unsplash.com/photo-1682530016814-6a1c1311cd6e?auto=format&fit=crop&w=900&q=80",
  energy:
    "https://images.unsplash.com/photo-1565802700195-01f42888e294?auto=format&fit=crop&w=900&q=80",
  skinBeauty:
    "https://images.unsplash.com/photo-1681390366427-251f73066f84?auto=format&fit=crop&w=900&q=80",
  wildcrafted:
    "https://images.unsplash.com/photo-1744020679176-2d7cd2208071?auto=format&fit=crop&w=900&q=80",
  howToUse:
    "https://images.unsplash.com/photo-1674864728789-d2690891d9f5?auto=format&fit=crop&w=900&q=80",
  goldComposition:
    "https://images.unsplash.com/photo-1615485500834-bc10199bc727?auto=format&fit=crop&w=1200&q=80",
  purpleComposition:
    "https://images.unsplash.com/photo-1727839202150-65675fbd0027?auto=format&fit=crop&w=1200&q=80",
  smoothieScene:
    "https://images.unsplash.com/photo-1565802700195-01f42888e294?auto=format&fit=crop&w=900&q=80",
  teaScene:
    "https://images.pexels.com/photos/4499241/pexels-photo-4499241.jpeg?auto=compress&cs=tinysrgb&w=900&q=80",
  skincareScene:
    "https://images.unsplash.com/photo-1681390366427-251f73066f84?auto=format&fit=crop&w=900&q=80",
  textureMacro:
    "https://images.pexels.com/photos/8789618/pexels-photo-8789618.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
};

export const BASES = [
  {
    id: "gold",
    name: "Gold Sea Moss",
    label: "Classic mineral-rich base",
    note: "Best value for everyday jars and pouches.",
    rgb: { r: 232, g: 214, b: 168 },
    swatch: "radial-gradient(circle at 30% 30%, #F4E6BE 0%, #D9BE82 55%, #A88541 100%)",
  },
  {
    id: "purple",
    name: "Purple Sea Moss",
    label: "Deeper colour, richer botanical character",
    note: "Premium base. Priced above gold.",
    rgb: { r: 178, g: 142, b: 188 },
    swatch: "radial-gradient(circle at 30% 30%, #DBC3DC 0%, #A37FB1 55%, #5C3F77 100%)",
  },
];
